import {
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type Ref,
} from 'react'
import { Button } from './Button'
import { Sheet } from './Sheet'
import { Symbol } from './Symbol'

interface DateTimeFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'value' | 'onChange' | 'type'
> {
  id: string
  label: string
  wert: string
  onWert: (wert: string) => void
  art: 'date' | 'time'
  hilfe?: string | undefined
  fehler?: string | undefined
  className?: string | undefined
  ref?: Ref<HTMLInputElement> | undefined
}

const zwei = (wert: number) => String(wert).padStart(2, '0')
const isoTag = (datum: Date) =>
  `${datum.getFullYear()}-${zwei(datum.getMonth() + 1)}-${zwei(datum.getDate())}`

function datumAusIso(wert: string): Date | null {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wert)
  if (!treffer) return null
  const datum = new Date(Number(treffer[1]), Number(treffer[2]) - 1, Number(treffer[3]))
  return isoTag(datum) === wert ? datum : null
}

const datumsAnzeige = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})
const monatsAnzeige = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' })

function Calendar({
  wert,
  min,
  max,
  onWaehlen,
}: {
  wert: string
  min?: string
  max?: string
  onWaehlen: (wert: string) => void
}) {
  const heute = new Date()
  const ausgewaehlt = datumAusIso(wert)
  const [monat, setMonat] = useState(
    () => new Date((ausgewaehlt ?? heute).getFullYear(), (ausgewaehlt ?? heute).getMonth(), 1),
  )
  const [fokusTag, setFokusTag] = useState(() => isoTag(ausgewaehlt ?? heute))
  const raster = useRef<HTMLDivElement>(null)
  const erster = new Date(monat.getFullYear(), monat.getMonth(), 1)
  const vorlauf = (erster.getDay() + 6) % 7
  const start = new Date(monat.getFullYear(), monat.getMonth(), 1 - vorlauf)
  const tage = Array.from({ length: 42 }, (_, index) => {
    const datum = new Date(start)
    datum.setDate(start.getDate() + index)
    return datum
  })

  useEffect(() => {
    raster.current
      ?.querySelector<HTMLButtonElement>(`[data-datum="${fokusTag}"]`)
      ?.focus({ preventScroll: true })
  }, [fokusTag, monat])

  const gesperrt = (iso: string) => (min ? iso < min : false) || (max ? iso > max : false)
  const setzeFokus = (datum: Date) => {
    setMonat(new Date(datum.getFullYear(), datum.getMonth(), 1))
    setFokusTag(isoTag(datum))
  }
  const bewegen = (iso: string, anzahl: number) => {
    const datum = datumAusIso(iso)
    if (!datum) return
    datum.setDate(datum.getDate() + anzahl)
    if (!gesperrt(isoTag(datum))) setzeFokus(datum)
  }
  const tasten = (event: KeyboardEvent<HTMLButtonElement>, datum: Date) => {
    const iso = isoTag(datum)
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      bewegen(iso, event.key === 'ArrowLeft' ? -1 : 1)
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      bewegen(iso, event.key === 'ArrowUp' ? -7 : 7)
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault()
      const wochentag = (datum.getDay() + 6) % 7
      bewegen(iso, event.key === 'Home' ? -wochentag : 6 - wochentag)
    } else if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault()
      const ziel = new Date(datum)
      ziel.setMonth(ziel.getMonth() + (event.key === 'PageUp' ? -1 : 1))
      setzeFokus(ziel)
    }
  }

  return (
    <div className="datumsauswahl">
      <div className="datumsauswahl__kopf">
        <button
          type="button"
          className="datumsauswahl__monat-knopf"
          aria-label="Vorheriger Monat"
          onClick={() => setMonat((alt) => new Date(alt.getFullYear(), alt.getMonth() - 1, 1))}
        >
          <Symbol name="chevron-left" />
        </button>
        <strong aria-live="polite">{monatsAnzeige.format(monat)}</strong>
        <button
          type="button"
          className="datumsauswahl__monat-knopf"
          aria-label="Nächster Monat"
          onClick={() => setMonat((alt) => new Date(alt.getFullYear(), alt.getMonth() + 1, 1))}
        >
          <Symbol name="chevron-right" />
        </button>
      </div>
      <div className="datumsauswahl__wochentage" aria-hidden="true">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((tag) => (
          <span key={tag}>{tag}</span>
        ))}
      </div>
      <div ref={raster} className="datumsauswahl__tage" role="grid" aria-label="Kalender">
        {tage.map((datum) => {
          const iso = isoTag(datum)
          const istGesperrt = gesperrt(iso)
          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              data-datum={iso}
              className="datumsauswahl__tag"
              data-fremder-monat={datum.getMonth() !== monat.getMonth() ? '' : undefined}
              aria-label={datumsAnzeige.format(datum)}
              aria-selected={iso === wert}
              aria-current={iso === isoTag(heute) ? 'date' : undefined}
              disabled={istGesperrt}
              tabIndex={fokusTag === iso && !istGesperrt ? 0 : -1}
              onFocus={() => setFokusTag(iso)}
              onKeyDown={(event) => tasten(event, datum)}
              onClick={() => onWaehlen(iso)}
            >
              {datum.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Eigener Datums-/Zeitwähler; das native Feld bleibt nur Formularzustand. */
export function DateTimeField({
  id,
  label,
  wert,
  onWert,
  art,
  hilfe,
  fehler,
  className,
  ref,
  disabled,
  required,
  autoFocus,
  min,
  max,
  ...rest
}: DateTimeFieldProps) {
  const beschreibung = fehler ?? hilfe
  const labelId = `${id}-label`
  const [offen, setOffen] = useState(false)
  const [zeit, setZeit] = useState(() => wert || '12:00')
  const [stunden, minuten] = (zeit || '12:00').split(':')
  const datum = datumAusIso(wert)
  const anzeige =
    art === 'date'
      ? datum
        ? datumsAnzeige.format(datum)
        : 'Datum auswählen'
      : wert || 'Uhrzeit auswählen'

  const oeffnen = () => {
    if (disabled) return
    if (art === 'time') setZeit(wert || '12:00')
    setOffen(true)
  }
  const zeitUebernehmen = () => {
    const stunde = zwei(Math.min(23, Math.max(0, Number(stunden) || 0)))
    const minute = zwei(Math.min(59, Math.max(0, Number(minuten) || 0)))
    onWert(`${stunde}:${minute}`)
    setOffen(false)
  }

  return (
    <div className={`tf tf--wahl${fehler ? ' tf--fehler' : ''}${className ? ` ${className}` : ''}`}>
      <input
        ref={ref}
        id={id}
        type={art}
        className="tf__nativ-zustand"
        value={wert}
        onChange={(event) => onWert(event.target.value)}
        disabled={disabled}
        required={required}
        min={min}
        max={max}
        tabIndex={-1}
        aria-hidden="true"
        {...rest}
      />
      <button
        id={`${id}-auswahl`}
        type="button"
        className="tf__eingabe tf__wahlknopf"
        aria-labelledby={labelId}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-describedby={beschreibung ? `${id}-hinweis` : undefined}
        aria-invalid={fehler ? true : undefined}
        aria-required={required || undefined}
        disabled={disabled}
        autoFocus={autoFocus}
        onClick={oeffnen}
      >
        <span id={`${id}-wert`} className="tf__wahlwert">
          {anzeige}
        </span>
        <Symbol name={art === 'date' ? 'calendar' : 'clock'} className="tf__pfeil" />
      </button>
      <label className="tf__label tf__label--fest" id={labelId} htmlFor={`${id}-auswahl`}>
        {label}
      </label>
      {beschreibung ? (
        <p className="tf__hinweis" id={`${id}-hinweis`} {...(fehler ? { role: 'alert' } : {})}>
          {beschreibung}
        </p>
      ) : null}

      <Sheet
        offen={offen}
        titel={art === 'date' ? `${label}: Datum auswählen` : `${label}: Uhrzeit auswählen`}
        onSchliessen={() => setOffen(false)}
      >
        {art === 'date' ? (
          <Calendar
            wert={wert}
            {...(typeof min === 'string' ? { min } : {})}
            {...(typeof max === 'string' ? { max } : {})}
            onWaehlen={(naechsterWert) => {
              onWert(naechsterWert)
              setOffen(false)
            }}
          />
        ) : (
          <div className="zeitauswahl">
            <label>
              <span>Stunde</span>
              <input
                className="zeitauswahl__zahl"
                type="number"
                inputMode="numeric"
                min="0"
                max="23"
                value={stunden}
                autoFocus
                onChange={(event) => setZeit(`${event.target.value}:${minuten}`)}
              />
            </label>
            <span className="zeitauswahl__trenner" aria-hidden="true">
              :
            </span>
            <label>
              <span>Minute</span>
              <input
                className="zeitauswahl__zahl"
                type="number"
                inputMode="numeric"
                min="0"
                max="59"
                value={minuten}
                onChange={(event) => setZeit(`${stunden}:${event.target.value}`)}
              />
            </label>
          </div>
        )}
        <div className="blatt__fuss-inline">
          {!required ? (
            <Button
              variant="text"
              onClick={() => {
                onWert('')
                setOffen(false)
              }}
            >
              Löschen
            </Button>
          ) : (
            <span />
          )}
          {art === 'date' ? (
            <Button
              variant="text"
              disabled={
                (Boolean(min) && isoTag(new Date()) < String(min)) ||
                (Boolean(max) && isoTag(new Date()) > String(max))
              }
              onClick={() => {
                onWert(isoTag(new Date()))
                setOffen(false)
              }}
            >
              Heute
            </Button>
          ) : (
            <Button onClick={zeitUebernehmen}>Übernehmen</Button>
          )}
        </div>
      </Sheet>
    </div>
  )
}
